import { useState } from 'react';
import { siteInfo } from '@/mocks/site';

const SUBMIT_URL = 'https://readdy.ai/api/form/daijsnrp14a1h3mr75j0';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    fd.delete('contact_alt');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => payload.append(key, String(value)));

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as { message?: string; detail?: string };
      const message =
        meta.message || (parsed?.message as string) || meta.detail || responseText || '';
      const code = (parsed?.code as string) || '';
      const isSpam = String(message).toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(message || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请检查连接后重试');
    }
  };

  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-content mx-auto px-4 md:px-6">
        <div className="relative rounded-lg overflow-hidden border border-background-200 bg-secondary-950">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 md:p-12">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent-500/40 text-accent-400 text-xs mb-5">
                <i className="ri-notification-3-line" />
                专题更新订阅
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 leading-snug">
                新专题上线时，第一时间告诉你
              </h2>
              <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed">
                留下邮箱，我们会在热门专题更新时发送简短提醒。不频繁打扰，随时可以退订。
              </p>
              <p className="mt-4 text-sm text-background-200/80 flex items-center gap-2">
                <i className="ri-mail-line text-accent-400" />
                也可以直接写信给我们：{siteInfo.email}
              </p>
            </div>

            <div className="p-8 md:p-12 bg-background-100">
              <form data-readdy-form onSubmit={onSubmit} noValidate>
                <label htmlFor="subscribe-email" className="block text-sm text-foreground-800 mb-2">
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400"
                />

                <label htmlFor="subscribe-topic" className="block text-sm text-foreground-800 mt-5 mb-2">
                  感兴趣的方向（选填）
                </label>
                <input
                  id="subscribe-topic"
                  type="text"
                  name="topic"
                  placeholder="例如：娱乐综艺、网红爆料"
                  className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400"
                />

                <input
                  type="text"
                  name="contact_alt"
                  className="form-hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="mt-6 w-full px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {status === 'loading' ? '提交中…' : '订阅更新提醒'}
                </button>

                {status === 'success' && (
                  <p className="mt-4 flex items-center gap-2 text-sm text-secondary-700">
                    <i className="ri-checkbox-circle-line" />
                    订阅成功，感谢关注，我们会尽快与新专题一起出现在你的邮箱。
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-4 flex items-center gap-2 text-sm text-primary-700">
                    <i className="ri-error-warning-line" />
                    {errorMsg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}